import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import { PanelVariableInputContent } from "../../lib/panel/panel-variable-input-content";
import type { ReturnDownloadLabelBlock } from "./types";

interface ReturnDownloadLabelPanelLabelProps {
  /** Which content field this input edits. */
  field: "label" | "bonDeRetour";
  titleKey: string;
  titleFallback: string;
  placeholderFallback: string;
}

/**
 * Editable label input for one of the two download-button cases (shipping label
 * vs. return slip). Modeled on the `button` block's label panel, but writes a
 * single named field (no href) and preserves the sibling field.
 */
export function ReturnDownloadLabelPanelLabel({
  field,
  titleKey,
  titleFallback,
  placeholderFallback,
}: ReturnDownloadLabelPanelLabelProps) {
  const blockProps = useBlockPanelPropsContext();
  const { t } = useTranslation("editor-blocks-library");
  const { formAPI } = blockProps;
  const block = blockProps.block as ReturnDownloadLabelBlock;

  const locale = blockProps.locale || blockProps?.defaultLocale;
  const content = block.content?.[locale];
  const currentText = content?.[field];

  const handleSave = useCallback(
    (text: string) => {
      formAPI.updateField(`${formAPI.blockPath}.content.${locale}`, {
        ...content,
        [field]: text,
      });
    },
    [formAPI, locale, field, content],
  );

  return (
    <PanelVariableInputContent
      triggerOnChange
      title={t(titleKey, titleFallback)}
      value={currentText ?? ""}
      onChange={handleSave}
      variables={blockProps.variables}
      id={`return-download-label-${field}`}
      placeholder={placeholderFallback}
      outputFormat="text"
    />
  );
}
